/**
 * F-148 §5.1 non-blocking review warning: a receipt with the same lines was posted for the same
 * supplier in the last 14 days. Shows a link to that receipt; never blocks posting.
 */
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { ReceivingIssue } from '@dukanti/shared/admin';
import { cn } from '@/lib/utils';
import { withRawValues } from '../lib/error-messages';

export interface SimilarReceiptWarningProps {
  readonly issue: ReceivingIssue<'SIMILAR_RECENT_RECEIPT'>;
  readonly className?: string;
}

function formatDate(value: string | number | undefined, lang: string): string {
  if (value === undefined) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat(lang, { dateStyle: 'medium' }).format(date);
}

export function SimilarReceiptWarning({
  issue,
  className,
}: SimilarReceiptWarningProps): React.JSX.Element {
  const { t, lang } = useTranslation();
  const receiptId = typeof issue.meta?.receiptId === 'string' ? issue.meta.receiptId : null;
  const postedAt = issue.meta?.postedAt;

  return (
    <section
      role="note"
      data-testid="similar-receipt-warning"
      className={cn(
        'flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-start text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/15 dark:text-amber-100',
        className,
      )}
    >
      <AlertTriangle aria-hidden="true" className="mt-0.5 size-5 shrink-0" strokeWidth={1.75} />
      <div className="space-y-2 text-sm">
        <p>
          {t(
            'admin.receiving.review.similarRecent',
            withRawValues({ date: formatDate(postedAt, lang) }),
          )}
        </p>
        {receiptId ? (
          <Link
            to={`/stock/receiving/receipts/${encodeURIComponent(receiptId)}`}
            className="inline-flex min-h-11 items-center font-medium underline underline-offset-4"
            data-testid="similar-receipt-link"
          >
            {t('admin.receiving.review.openSimilar')}
          </Link>
        ) : null}
      </div>
    </section>
  );
}
